import { Box, Typography } from '@mui/material'
import LocalLibrary from '@mui/icons-material/LocalLibrary'
import { GridColDef } from '@mui/x-data-grid-premium'
import distanceInWords from 'date-fns/formatDistance'
import format from 'date-fns/format'
import { UserContractor } from 'src/api'
import { isViewOnlyRole } from 'src/helpers'
import { ClosedEye } from '../icons'
import { InnerLink } from '../ui'

namespace UserContractorColumns {
  export const Name: GridColDef<UserContractor> = {
    field: 'name',
    headerName: 'Name',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => {
      const isViewOnly = isViewOnlyRole(row.role)
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <InnerLink to={`/users/${row.userId}`}>{row.name || '-'}</InnerLink>
          {row.isArchived && (
            <Box display="flex" title="Archived User">
              <ClosedEye />
            </Box>
          )}
          {isViewOnly && <LocalLibrary fontSize="small" titleAccess="View-only user" />}
        </Box>
      )
    }
  }

  export const Email: GridColDef<UserContractor> = {
    field: 'email',
    headerName: 'Email',
    minWidth: 150,
    flex: 1
  }

  export const CreatedAt: GridColDef<UserContractor> = {
    field: 'createdAt',
    headerName: 'Created At',
    minWidth: 180,
    type: 'date',
    valueFormatter: ({ value }) => (value ? format(new Date(value), 'PPpp') : '-')
  }

  export const Phone: GridColDef<UserContractor> = {
    field: 'user.phone',
    headerName: 'Phone',
    minWidth: 150,
    valueGetter: ({ row }) => row.user?.phone || '-'
  }

  export const LastActive: GridColDef<UserContractor> = {
    field: 'user.lastActive',
    headerName: 'Last Active',
    minWidth: 150,
    valueGetter: ({ row }) => row.user?.lastActive,
    valueFormatter: ({ value }) => (value ? distanceInWords(new Date(value), new Date()) : 'Never')
  }

  export const LastAction: GridColDef<UserContractor> = {
    field: 'user.lastAction',
    headerName: 'Last Action',
    minWidth: 250,
    valueGetter: ({ row }) => row.user?.lastAction,
    renderCell: ({ row }) => {
      const lastAction = row.user?.lastAction
      if (!lastAction) return <Typography variant="body2">-</Typography>
      return (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {lastAction.action} ({distanceInWords(new Date(lastAction.date), new Date())})
        </Typography>
      )
    }
  }

  export const Role: GridColDef<UserContractor> = {
    field: 'role.displayName',
    headerName: 'Role',
    minWidth: 130,
    flex: 1,
    valueGetter: ({ row }) => row.role?.displayName || row.role?.name || '-'
  }

  export const ContractorName: GridColDef<UserContractor> = {
    field: 'contractor.name',
    headerName: 'Contractor',
    minWidth: 150,
    flex: 1,
    renderCell: ({ row }) => <InnerLink to={`/contractors/${row.contractorId}`}>{row.contractor?.name || '-'}</InnerLink>
  }

  export const PlainName: GridColDef<UserContractor> = {
    field: 'plainName',
    headerName: 'Name',
    minWidth: 150,
    flex: 1,
    valueGetter: ({ row }) => row.name || '-'
  }

  export const Status: GridColDef<UserContractor> = {
    field: 'isArchived',
    headerName: 'Status',
    minWidth: 150,
    flex: 1,
    valueGetter: ({ row }) => (row.isArchived ? 'Archived' : 'Active')
  }
}

export default UserContractorColumns
